import React from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  Banknote,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleGauge,
  ClipboardCheck,
  Clock3,
  Coins,
  FileCheck2,
  FileText,
  Gauge,
  Leaf,
  LoaderCircle,
  NotebookPen,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Sparkles,
  Trash2,
  TrendingDown,
  WalletCards,
  X,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useToast } from "../../../components/feedback/ToastProvider";
import "./RenovationProjectMonitoringStep.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const STEP_CODE = "MONITORING";

const EMPTY_NOTE = {
  tag: "UPDATE",
  note: "",
};

const EMPTY_MODIFICATION = {
  modification_types: [],
  description: "",
};

const EMPTY_FINAL_EPC = {
  certifier_name: "",
  professional_accreditation: "",
  certificate_date: "",
  sector: "",
  energy_rating: "",
  emission_rating: "",
  primary_non_renewable_energy_consumption: "",
  co2_emissions: "",
  notes: "",
};

const EPC_RATING_OPTIONS = [
  { value: "A_PLUS", label: "A+" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "D", label: "D" },
  { value: "E", label: "E" },
  { value: "F", label: "F" },
  { value: "G", label: "G" },
];

const EPC_ACCREDITATION_OPTIONS = [
  { value: "ARCHITECT", label: "Architect" },
  { value: "TECHNICAL_ARCHITECT", label: "Technical architect" },
  { value: "ENGINEER", label: "Engineer" },
  { value: "TECHNICAL_ENGINEER", label: "Technical engineer" },
];

const EPC_SECTOR_OPTIONS = [
  { value: "BUILDING_REHABILITATION", label: "Building rehabilitation" },
  { value: "RENEWABLE_ENERGIES", label: "Renewable energies" },
];

const MODIFICATION_TYPES = ["SCOPE", "TIMELINE", "COST", "EXTENSION"];

/* =========================================================
   HELPERS
   ========================================================= */

function findStep(steps = []) {
  return (
    steps.find((step) => step.step_code === STEP_CODE) || {
      step_code: STEP_CODE,
      step_order: 8,
      status: "NOT_STARTED",
      is_optional: false,
      visible_to: "HB_OSS",
      editable_by: "HB_OSS",
    }
  );
}

function statusClass(value) {
  return String(value || "NOT_STARTED")
    .trim()
    .toLowerCase()
    .replaceAll("_", "-");
}

function clampPercentage(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.min(parsed, 100));
}

function numericValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMoney(value, currency = "EUR", locale = "en") {
  const parsed = numericValue(value);

  if (parsed === null) {
    return "—";
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 2,
    }).format(parsed);
  } catch {
    return `${parsed.toLocaleString(locale)} ${currency || "EUR"}`;
  }
}

function formatPercentage(value, locale = "en") {
  const parsed = numericValue(value);

  if (parsed === null) {
    return "—";
  }

  return `${parsed.toLocaleString(locale, {
    maximumFractionDigits: 2,
  })}%`;
}

function formatDate(value, locale = "en") {
  if (!value) {
    return "—";
  }

  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value, locale = "en") {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function serializeDate(value) {
  return value || null;
}

function sanitizeDecimalInput(value, maxDecimals = 2) {
  let cleaned = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "");

  if (!cleaned) {
    return "";
  }

  const dotIndex = cleaned.indexOf(".");

  let integerPart = dotIndex === -1 ? cleaned : cleaned.slice(0, dotIndex);

  let decimalPart =
    dotIndex === -1
      ? ""
      : cleaned
          .slice(dotIndex + 1)
          .replace(/\./g, "")
          .slice(0, maxDecimals);

  integerPart = integerPart.replace(/^0+(?=\d)/, "");

  if (!integerPart && dotIndex !== -1) {
    integerPart = "0";
  }

  return dotIndex === -1 ? integerPart : `${integerPart}.${decimalPart}`;
}

function formatDecimalInput(value) {
  const raw = String(value ?? "").replace(/,/g, "");

  if (!raw) {
    return "";
  }

  const hasDecimalPoint = raw.includes(".");
  const [integerPart, decimalPart = ""] = raw.split(".");

  const formattedInteger = (integerPart || "0").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ",",
  );

  return hasDecimalPoint
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
}

function displayEnergyRating(value) {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  return normalized === "A_PLUS" ? "A+" : normalized || "—";
}

function resolveApiUrl(url) {
  if (!url) {
    return "";
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `${API_BASE}${String(url).startsWith("/") ? "" : "/"}${url}`;
}

function parseApiError(data, fallback) {
  if (!data) {
    return fallback;
  }

  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }

  if (data.errors && typeof data.errors === "object") {
    const first = Object.values(data.errors).flat().find(Boolean);

    if (first) {
      return String(first);
    }
  }

  return fallback;
}

function energyRatingClass(value) {
  const normalized = String(value || "")
    .toUpperCase()
    .replace("+", "_PLUS");

  return normalized
    ? `monitoring-epc-grade--${normalized.toLowerCase()}`
    : "monitoring-epc-grade--unknown";
}

function humanizeCode(value) {
  return String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function normalizeActorRole(value) {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  if (normalized === "HOMEOWNER" || normalized === "HOMEBUYER") {
    return "HB";
  }

  if (normalized === "OSS" || normalized === "OSS_REPRESENTATIVE") {
    return "OSS";
  }

  return normalized;
}

function getFundingSourceType(source) {
  return String(
    source?.source_type ||
      source?.funding_type?.code ||
      source?.funding_type ||
      source?.type ||
      source?.category ||
      "OTHER",
  )
    .trim()
    .toUpperCase();
}

function isLenderFundingSource(source) {
  const sourceType = getFundingSourceType(source);

  if (
    source?.is_lender_financing === true ||
    source?.funding_type?.is_lender_financing === true
  ) {
    return true;
  }

  return ["LENDER_FINANCING", "LENDER_LOAN", "LOAN", "BANK_LOAN"].includes(
    sourceType,
  );
}

function sourceAvailableAmount(source) {
  return (
    numericValue(
      source?.available_amount ??
        source?.renovation_amount ??
        source?.approved_amount ??
        source?.confirmed_amount ??
        source?.requested_amount ??
        source?.amount,
    ) || 0
  );
}

function normalizeStep6FundingItem(item, index, t) {
  const type = String(item?.category || item?.source_type || "OTHER")
    .trim()
    .toUpperCase();

  const status = String(item?.status || "")
    .trim()
    .toUpperCase();

  const amount =
    numericValue(
      item?.granted_amount ?? item?.approved_amount ?? item?.amount,
    ) || 0;

  return {
    key: item?.funding_source_id || item?.id || `step6-${index}`,

    type,

    name:
      item?.funding_type_name ||
      item?.name ||
      t(`finance.sources.${type}`, humanizeCode(type)),

    provider: item?.provider_name || "",

    status,

    availableAmount: amount,

    /*
     * Step 6 tells us the available / granted amount.
     * It does not expose the invoice allocation per source.
     * Keep this null instead of pretending it is zero.
     */
    allocatedAmount: null,

    isLender: false,

    countsTowardAvailable: status === "GRANTED",
  };
}

function buildProjectFundingSources({
  tracker,
  dashboard,
  project,
  financing,
  supplementaryFundingItems,
  t,
}) {
  const candidates = [
    tracker?.funding_source_breakdown,
    dashboard?.funding_sources,
    dashboard?.project_funding_sources,
    project?.funding_sources,
    financing?.funding_sources,
  ];

  const explicitSources =
    candidates.find((value) => Array.isArray(value) && value.length > 0) || [];

  let normalized = explicitSources
    .map((source, index) => {
      const type = getFundingSourceType(source);

      const availableAmount = sourceAvailableAmount(source);

      const rawAllocated =
        source?.allocated_amount ??
        source?.invoice_allocated_amount ??
        source?.used_amount ??
        source?.amount_allocated;

      const allocatedAmount =
        rawAllocated === null ||
        rawAllocated === undefined ||
        rawAllocated === ""
          ? null
          : numericValue(rawAllocated) || 0;

      const status = String(source?.status || "")
        .trim()
        .toUpperCase();

      return {
        key:
          source?.id ||
          source?.funding_source_id ||
          `${type}-${source?.name || index}`,

        type,

        name:
          source?.name ||
          source?.label ||
          source?.source_name ||
          source?.funding_type?.name ||
          t(`finance.sources.${type}`, humanizeCode(type)),

        provider: source?.provider_name || source?.provider || "",

        status,

        availableAmount,

        allocatedAmount,

        isLender: isLenderFundingSource(source),

        countsTowardAvailable: !["REJECTED", "CANCELLED"].includes(status),
      };
    })
    .filter(
      (source) =>
        source.availableAmount > 0 ||
        (source.allocatedAmount !== null && source.allocatedAmount > 0),
    );

  /*
   * The current Monitoring backend may not yet expose the
   * project funding-source list. Step 6 already does, so use
   * its GRANTED subsidy / grant / soft-loan records as the
   * canonical fallback.
   */
  if (
    !normalized.some((source) => !source.isLender) &&
    Array.isArray(supplementaryFundingItems)
  ) {
    normalized = [
      ...normalized,

      ...supplementaryFundingItems
        .map((item, index) => normalizeStep6FundingItem(item, index, t))
        .filter(
          (source) =>
            source.availableAmount > 0 &&
            !["REJECTED", "NOT_APPLIED"].includes(source.status),
        ),
    ];
  }

  const lenderAvailable =
    numericValue(financing?.renovation_financing_amount) || 0;

  const lenderAllocated =
    numericValue(tracker?.lender_financed_invoice_amount) || 0;

  const existingLenderIndex = normalized.findIndex((source) => source.isLender);

  if (lenderAvailable > 0) {
    if (existingLenderIndex >= 0) {
      normalized[existingLenderIndex] = {
        ...normalized[existingLenderIndex],

        availableAmount: lenderAvailable,

        allocatedAmount:
          normalized[existingLenderIndex].allocatedAmount ?? lenderAllocated,

        countsTowardAvailable: true,
      };
    } else {
      normalized.unshift({
        key: "LENDER_FINANCING",

        type: "LENDER_FINANCING",

        name:
          financing?.lender?.name ||
          t("finance.sources.LENDER_FINANCING", "Lender financing"),

        provider: financing?.lender?.name || "",

        status: financing?.maturity || "",

        availableAmount: lenderAvailable,

        allocatedAmount: lenderAllocated,

        isLender: true,

        countsTowardAvailable: true,
      });
    }
  }

  return normalized;
}

function getEntryCreatorRole(entry) {
  return normalizeActorRole(
    entry?.permissions?.creator_role ||
      entry?.created_by?.role ||
      entry?.created_by?.actor_role ||
      entry?.created_by_role ||
      entry?.actor_role ||
      "",
  );
}

function canManageEntry(entry, viewerRole, action = "edit") {
  if (!entry) {
    return false;
  }

  const explicitPermission =
    action === "delete"
      ? entry?.permissions?.can_delete
      : entry?.permissions?.can_edit;

  if (explicitPermission === true || explicitPermission === false) {
    return explicitPermission;
  }

  const normalizedViewerRole = normalizeActorRole(viewerRole);

  const creatorRole = getEntryCreatorRole(entry);

  /*
   * Safe fallback:
   * If the backend does not tell us who created the entry,
   * do not show edit/delete controls. This prevents an HB
   * from editing an OSS item (and vice versa) by accident.
   */
  if (!normalizedViewerRole || !creatorRole) {
    return false;
  }

  return normalizedViewerRole === creatorRole;
}

/* =========================================================
   STATUS PILL
   ========================================================= */

function StepStatusPill({ status, t }) {
  const normalized = String(status || "NOT_STARTED").toUpperCase();

  const labels = {
    NOT_STARTED: t("status.notStarted", "Not started"),
    IN_PROGRESS: t("status.inProgress", "In progress"),
    COMPLETED: t("status.completed", "Completed"),
    SKIPPED: t("status.skipped", "Skipped"),
  };

  return (
    <span
      className={`monitoring-step__status-pill monitoring-step__status-pill--${statusClass(
        normalized,
      )}`}
    >
      {labels[normalized] || humanizeCode(normalized)}
    </span>
  );
}

/* =========================================================
   SECTION HEADING
   ========================================================= */

function SectionHeading({ icon: Icon, title, description, trailing = null }) {
  return (
    <div className="monitoring-section__heading">
      <div className="monitoring-section__heading-main">
        <span className="monitoring-section__icon" aria-hidden="true">
          <Icon size={17} />
        </span>

        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
      </div>

      {trailing ? (
        <div className="monitoring-section__trailing">{trailing}</div>
      ) : null}
    </div>
  );
}

/* =========================================================
   METRIC
   ========================================================= */

function MetricCard({ icon: Icon, label, value, meta, emphasis = false }) {
  return (
    <article
      className={`monitoring-metric ${emphasis ? "monitoring-metric--emphasis" : ""}`}
    >
      <span className="monitoring-metric__icon">
        <Icon size={17} />
      </span>

      <div className="monitoring-metric__content">
        <span>{label}</span>
        <strong>{value}</strong>
        {meta ? <small>{meta}</small> : null}
      </div>
    </article>
  );
}

/* =========================================================
   CONFIRM MODAL
   ========================================================= */

function CompletionModal({ open, onClose, onConfirm, busy, warnings, t }) {
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [busy, onClose, open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="monitoring-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <section
        className="monitoring-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="monitoring-complete-title"
      >
        <button
          type="button"
          className="monitoring-modal__close"
          onClick={onClose}
          disabled={busy}
          aria-label={t("common.close", "Close")}
        >
          <X size={18} />
        </button>

        <span className="monitoring-modal__symbol">
          <CheckCircle2 size={25} />
        </span>

        <h2 id="monitoring-complete-title">
          {t("completion.modal.title", "Complete renovation project?")}
        </h2>

        <p>
          {t(
            "completion.modal.description",
            "This will mark Monitoring and the renovation project as completed. Monitoring records will become read-only.",
          )}
        </p>

        {warnings?.length ? (
          <div className="monitoring-modal__warning">
            <AlertTriangle size={17} />

            <div>
              <strong>
                {t("completion.modal.warningTitle", "Before you continue")}
              </strong>

              {warnings.includes("FINAL_EPC_NOT_AVAILABLE") ? (
                <span>
                  {t(
                    "completion.warnings.finalEpcMissing",
                    "No Final EPC information is available yet.",
                  )}
                </span>
              ) : null}

              {warnings.includes("FINAL_EPC_DOCUMENT_NOT_AVAILABLE") ? (
                <span>
                  {t(
                    "completion.warnings.finalEpcDocumentMissing",
                    "The Final EPC record has no evidence document attached.",
                  )}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="monitoring-modal__actions">
          <button
            type="button"
            className="monitoring-button monitoring-button--secondary"
            onClick={onClose}
            disabled={busy}
          >
            {t("common.cancel", "Cancel")}
          </button>

          <button
            type="button"
            className="monitoring-button monitoring-button--complete"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <LoaderCircle size={16} className="monitoring-spin" />
            ) : (
              <Check size={16} />
            )}

            {busy
              ? t("completion.modal.completing", "Completing...")
              : t("completion.modal.confirm", "Complete project")}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

/* =========================================================
   MAIN
   ========================================================= */

export default function RenovationProjectMonitoringStep() {
  const { t, i18n } = useTranslation("renovation-project-monitoring");
  const locale = i18n.resolvedLanguage || i18n.language || "en";

  const { getAccessTokenSilently } = useAuth0();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const {
    project,
    steps,
    basePath,
    viewerRole,
    registerStepSaveHandler,
    setStepDirty,
    refreshProject,
  } = useOutletContext();

  const step = findStep(steps);

  /* =======================================================
     SERVER STATE
     ======================================================= */

  const [dashboard, setDashboard] = React.useState(null);

  const [supplementaryFundingItems, setSupplementaryFundingItems] =
    React.useState([]);

  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);

  /* =======================================================
     MONITORING DATE FORM
     ======================================================= */

  const [dates, setDates] = React.useState({
    expected_completion_date: "",
    actual_completion_date: "",
  });

  const [savedDates, setSavedDates] = React.useState({
    expected_completion_date: "",
    actual_completion_date: "",
  });

  const [savingDates, setSavingDates] = React.useState(false);

  /* =======================================================
     FINAL EPC FORM / DOCUMENT
     ======================================================= */

  const [finalEpcForm, setFinalEpcForm] = React.useState({
    ...EMPTY_FINAL_EPC,
  });

  const [savedFinalEpcForm, setSavedFinalEpcForm] = React.useState({
    ...EMPTY_FINAL_EPC,
  });

  const [savingFinalEpc, setSavingFinalEpc] = React.useState(false);
  const [uploadingFinalEpc, setUploadingFinalEpc] = React.useState(false);
  const [deletingFinalEpc, setDeletingFinalEpc] = React.useState(false);

  const finalEpcFileInputRef = React.useRef(null);

  /* =======================================================
     NOTE FORM / EDIT
     ======================================================= */

  const [noteForm, setNoteForm] = React.useState(EMPTY_NOTE);
  const [noteBusy, setNoteBusy] = React.useState(false);
  const [editingNoteId, setEditingNoteId] = React.useState(null);
  const [editingNote, setEditingNote] = React.useState(EMPTY_NOTE);

  /* =======================================================
     MODIFICATION FORM / EDIT
     ======================================================= */

  const [modificationForm, setModificationForm] =
    React.useState(EMPTY_MODIFICATION);
  const [modificationBusy, setModificationBusy] = React.useState(false);
  const [editingModificationId, setEditingModificationId] =
    React.useState(null);
  const [editingModification, setEditingModification] =
    React.useState(EMPTY_MODIFICATION);

  /* =======================================================
     COMPLETION
     ======================================================= */

  const [completionModalOpen, setCompletionModalOpen] = React.useState(false);
  const [completionBusy, setCompletionBusy] = React.useState(false);

  const stepStatus = dashboard?.step_status || step.status || "NOT_STARTED";
  const projectStatus =
    dashboard?.project_status || project?.status || "IN_PROGRESS";
  const viewer = dashboard?.viewer || {};

  const effectiveViewerRole = normalizeActorRole(viewer?.role || viewerRole);

  const canEdit = Boolean(
    viewer.can_edit ?? ["HB", "OSS"].includes(effectiveViewerRole),
  );

  const isCompleted = projectStatus === "COMPLETED";

  const datesDirty =
    dates.expected_completion_date !== savedDates.expected_completion_date ||
    dates.actual_completion_date !== savedDates.actual_completion_date;

  const finalEpcDirty =
    JSON.stringify(finalEpcForm) !== JSON.stringify(savedFinalEpcForm);

  /* =======================================================
     API
     ======================================================= */

  const apiRequest = React.useCallback(
    async (path, { method = "GET", body } = {}) => {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          parseApiError(
            data,
            t("errors.requestFailed", "The request could not be completed."),
          ),
        );
      }

      return data;
    },
    [getAccessTokenSilently, t],
  );

  const applyDashboard = React.useCallback(
    (data) => {
      setDashboard(data);

      const nextDates = {
        expected_completion_date: data?.expected_completion_date || "",
        actual_completion_date: data?.actual_completion_date || "",
      };

      const finalEpc = data?.energy_performance?.post_renovation || {};

      const nextFinalEpc = {
        certifier_name: finalEpc?.certifier_name || "",
        professional_accreditation: finalEpc?.professional_accreditation || "",
        certificate_date: finalEpc?.certificate_date || "",
        sector: finalEpc?.sector || "",
        energy_rating: finalEpc?.energy_rating || "",
        emission_rating: finalEpc?.emission_rating || "",
        primary_non_renewable_energy_consumption:
          finalEpc?.primary_non_renewable_energy_consumption ?? "",
        co2_emissions: finalEpc?.co2_emissions ?? "",
        notes: finalEpc?.notes || "",
      };

      setDates(nextDates);
      setSavedDates(nextDates);
      setFinalEpcForm(nextFinalEpc);
      setSavedFinalEpcForm(nextFinalEpc);
      setStepDirty?.(false);
    },
    [setStepDirty],
  );

  const loadMonitoring = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!project?.id) {
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setLoadError("");

      try {
        const [data, subsidiesData] = await Promise.all([
          apiRequest(`/api/projects/${project.id}/monitoring/`),

          apiRequest(`/api/projects/${project.id}/subsidies-grants/`).catch(
            (error) => {
              /*
               * Monitoring itself should still load if the
               * supplementary-funding endpoint temporarily
               * fails. We simply omit those funding cards.
               */
              console.warn(
                "Failed to load supplementary funding for Monitoring:",
                error,
              );

              return {
                items: [],
              };
            },
          ),
        ]);

        applyDashboard(data);

        setSupplementaryFundingItems(
          Array.isArray(subsidiesData?.items) ? subsidiesData.items : [],
        );

        if (refreshProject) {
          await refreshProject();
        }
      } catch (error) {
        console.error("Failed to load Monitoring:", error);

        if (!silent) {
          setLoadError(
            error?.message ||
              t("errors.loadFailed", "Failed to load monitoring information."),
          );
        } else {
          showError?.(
            error?.message ||
              t(
                "errors.refreshFailed",
                "Failed to refresh monitoring information.",
              ),
          );
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiRequest, applyDashboard, project?.id, refreshProject, showError, t],
  );

  React.useEffect(() => {
    loadMonitoring();
  }, [loadMonitoring]);

  /* =======================================================
     SAVE DATES
     ======================================================= */

  const saveDates = React.useCallback(
    async ({ silentSuccess = false } = {}) => {
      if (!project?.id || !canEdit || !datesDirty) {
        setStepDirty?.(false);
        return true;
      }

      setSavingDates(true);

      try {
        const data = await apiRequest(
          `/api/projects/${project.id}/monitoring/`,
          {
            method: "PATCH",
            body: {
              expected_completion_date: serializeDate(
                dates.expected_completion_date,
              ),
              actual_completion_date: serializeDate(
                dates.actual_completion_date,
              ),
            },
          },
        );

        applyDashboard(data);

        if (refreshProject) {
          await refreshProject();
        }

        if (!silentSuccess) {
          showSuccess?.(
            t("dates.saved", "Monitoring dates saved successfully."),
          );
        }

        return true;
      } catch (error) {
        console.error("Failed to save Monitoring dates:", error);

        showError?.(
          error?.message ||
            t("dates.saveFailed", "Failed to save monitoring dates."),
        );

        return false;
      } finally {
        setSavingDates(false);
      }
    },
    [
      apiRequest,
      applyDashboard,
      canEdit,
      dates.actual_completion_date,
      dates.expected_completion_date,
      datesDirty,
      project?.id,
      refreshProject,
      setStepDirty,
      showError,
      showSuccess,
      t,
    ],
  );

  /* =======================================================
     FINAL EPC
     ======================================================= */

  const saveFinalEpc = React.useCallback(
    async ({ silentSuccess = false } = {}) => {
      if (!project?.id || !canEdit || !finalEpcDirty || isCompleted) {
        return true;
      }

      setSavingFinalEpc(true);

      try {
        const data = await apiRequest(
          `/api/projects/${project.id}/monitoring/final-epc/`,
          {
            method: "PATCH",
            body: {
              certifier_name: finalEpcForm.certifier_name.trim(),
              professional_accreditation:
                finalEpcForm.professional_accreditation || "",
              certificate_date: serializeDate(finalEpcForm.certificate_date),
              sector: finalEpcForm.sector || "",
              energy_rating: finalEpcForm.energy_rating || "",
              emission_rating: finalEpcForm.emission_rating || "",
              primary_non_renewable_energy_consumption:
                finalEpcForm.primary_non_renewable_energy_consumption || null,
              co2_emissions: finalEpcForm.co2_emissions || null,
              notes: finalEpcForm.notes.trim(),
            },
          },
        );

        applyDashboard(data);

        if (refreshProject) {
          await refreshProject();
        }

        if (!silentSuccess) {
          showSuccess?.(
            t("energy.saved", "Final EPC information saved successfully."),
          );
        }

        return true;
      } catch (error) {
        console.error("Failed to save Final EPC:", error);
        showError?.(
          error?.message ||
            t("energy.saveFailed", "Failed to save Final EPC information."),
        );
        return false;
      } finally {
        setSavingFinalEpc(false);
      }
    },
    [
      apiRequest,
      applyDashboard,
      canEdit,
      finalEpcDirty,
      finalEpcForm,
      isCompleted,
      project?.id,
      refreshProject,
      showError,
      showSuccess,
      t,
    ],
  );

  const uploadFinalEpcDocument = React.useCallback(
    async (file) => {
      if (!project?.id || !file || !canEdit || isCompleted) {
        return;
      }

      setUploadingFinalEpc(true);

      try {
        const token = await getAccessTokenSilently();
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${API_BASE}/api/projects/${project.id}/monitoring/final-epc/document/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
            body: formData,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            parseApiError(
              data,
              t("energy.uploadFailed", "Failed to upload the Final EPC."),
            ),
          );
        }

        applyDashboard(data);

        if (finalEpcFileInputRef.current) {
          finalEpcFileInputRef.current.value = "";
        }

        if (refreshProject) {
          await refreshProject();
        }

        showSuccess?.(t("energy.uploaded", "Final EPC uploaded successfully."));
      } catch (error) {
        console.error("Failed to upload Final EPC:", error);
        showError?.(
          error?.message ||
            t("energy.uploadFailed", "Failed to upload the Final EPC."),
        );
      } finally {
        setUploadingFinalEpc(false);
      }
    },
    [
      applyDashboard,
      canEdit,
      getAccessTokenSilently,
      isCompleted,
      project?.id,
      refreshProject,
      showError,
      showSuccess,
      t,
    ],
  );

  const deleteFinalEpcDocument = React.useCallback(async () => {
    if (!project?.id || !canEdit || isCompleted || deletingFinalEpc) {
      return;
    }

    setDeletingFinalEpc(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/monitoring/final-epc/document/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          parseApiError(
            data,
            t(
              "energy.deleteFailed",
              "Failed to remove the Final EPC document.",
            ),
          ),
        );
      }

      applyDashboard(data);

      showSuccess?.(t("energy.deleted", "Final EPC document removed."));
    } catch (error) {
      console.error("Failed to delete Final EPC document:", error);
      showError?.(
        error?.message ||
          t("energy.deleteFailed", "Failed to remove the Final EPC document."),
      );
    } finally {
      setDeletingFinalEpc(false);
    }
  }, [
    applyDashboard,
    canEdit,
    deletingFinalEpc,
    getAccessTokenSilently,
    isCompleted,
    project?.id,
    showError,
    showSuccess,
    t,
  ]);

  React.useEffect(() => {
    setStepDirty?.(datesDirty || finalEpcDirty);
  }, [datesDirty, finalEpcDirty, setStepDirty]);

  React.useEffect(() => {
    if (!registerStepSaveHandler) {
      return undefined;
    }

    return registerStepSaveHandler(async () => {
      if (datesDirty) {
        const datesSaved = await saveDates({ silentSuccess: true });

        if (!datesSaved) {
          return false;
        }
      }

      if (finalEpcDirty) {
        const epcSaved = await saveFinalEpc({ silentSuccess: true });

        if (!epcSaved) {
          return false;
        }
      }

      return true;
    });
  }, [
    datesDirty,
    finalEpcDirty,
    registerStepSaveHandler,
    saveDates,
    saveFinalEpc,
  ]);

  /* =======================================================
     NOTES
     ======================================================= */

  const createNote = async () => {
    const note = noteForm.note.trim();

    if (!note || !project?.id) {
      showError?.(t("notes.validation.note", "Enter a diary note first."));
      return;
    }

    setNoteBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/diary-notes/`,
        {
          method: "POST",
          body: {
            tag: noteForm.tag,
            note,
          },
        },
      );

      applyDashboard(data);
      setNoteForm(EMPTY_NOTE);

      if (refreshProject) {
        await refreshProject();
      }

      showSuccess?.(t("notes.created", "Diary note added."));
    } catch (error) {
      console.error("Failed to add diary note:", error);
      showError?.(
        error?.message || t("notes.createFailed", "Failed to add diary note."),
      );
    } finally {
      setNoteBusy(false);
    }
  };

  const startEditingNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNote({
      tag: note.tag || "UPDATE",
      note: note.note || "",
    });
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditingNote(EMPTY_NOTE);
  };

  const updateNote = async (noteId) => {
    if (!editingNote.note.trim()) {
      showError?.(t("notes.validation.note", "Enter a diary note first."));
      return;
    }

    setNoteBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/diary-notes/${noteId}/`,
        {
          method: "PATCH",
          body: {
            tag: editingNote.tag,
            note: editingNote.note.trim(),
          },
        },
      );

      applyDashboard(data);
      cancelEditingNote();

      showSuccess?.(t("notes.updated", "Diary note updated."));
    } catch (error) {
      console.error("Failed to update diary note:", error);
      showError?.(
        error?.message ||
          t("notes.updateFailed", "Failed to update diary note."),
      );
    } finally {
      setNoteBusy(false);
    }
  };

  const deleteNote = async (noteId) => {
    setNoteBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/diary-notes/${noteId}/`,
        {
          method: "DELETE",
        },
      );

      applyDashboard(data);

      if (editingNoteId === noteId) {
        cancelEditingNote();
      }

      showSuccess?.(t("notes.deleted", "Diary note deleted."));
    } catch (error) {
      console.error("Failed to delete diary note:", error);
      showError?.(
        error?.message ||
          t("notes.deleteFailed", "Failed to delete diary note."),
      );
    } finally {
      setNoteBusy(false);
    }
  };

  /* =======================================================
     MODIFICATIONS
     ======================================================= */

  const toggleModificationType = (type, editing = false) => {
    const setter = editing ? setEditingModification : setModificationForm;

    setter((current) => {
      const exists = current.modification_types.includes(type);

      return {
        ...current,
        modification_types: exists
          ? current.modification_types.filter((item) => item !== type)
          : [...current.modification_types, type],
      };
    });
  };

  const createModification = async () => {
    const description = modificationForm.description.trim();

    if (!description || !project?.id) {
      showError?.(
        t(
          "modifications.validation.description",
          "Enter a description for the modification.",
        ),
      );
      return;
    }

    setModificationBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/modifications/`,
        {
          method: "POST",
          body: {
            modification_types: modificationForm.modification_types,
            description,
          },
        },
      );

      applyDashboard(data);
      setModificationForm(EMPTY_MODIFICATION);

      if (refreshProject) {
        await refreshProject();
      }

      showSuccess?.(t("modifications.created", "Project modification added."));
    } catch (error) {
      console.error("Failed to add project modification:", error);
      showError?.(
        error?.message ||
          t(
            "modifications.createFailed",
            "Failed to add the project modification.",
          ),
      );
    } finally {
      setModificationBusy(false);
    }
  };

  const startEditingModification = (item) => {
    setEditingModificationId(item.id);
    setEditingModification({
      modification_types: Array.isArray(item.modification_types)
        ? item.modification_types
        : [],
      description: item.description || "",
    });
  };

  const cancelEditingModification = () => {
    setEditingModificationId(null);
    setEditingModification(EMPTY_MODIFICATION);
  };

  const updateModification = async (modificationId) => {
    if (!editingModification.description.trim()) {
      showError?.(
        t(
          "modifications.validation.description",
          "Enter a description for the modification.",
        ),
      );
      return;
    }

    setModificationBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/modifications/${modificationId}/`,
        {
          method: "PATCH",
          body: {
            modification_types: editingModification.modification_types,
            description: editingModification.description.trim(),
          },
        },
      );

      applyDashboard(data);
      cancelEditingModification();

      showSuccess?.(
        t("modifications.updated", "Project modification updated."),
      );
    } catch (error) {
      console.error("Failed to update project modification:", error);
      showError?.(
        error?.message ||
          t(
            "modifications.updateFailed",
            "Failed to update the project modification.",
          ),
      );
    } finally {
      setModificationBusy(false);
    }
  };

  const deleteModification = async (modificationId) => {
    setModificationBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/modifications/${modificationId}/`,
        {
          method: "DELETE",
        },
      );

      applyDashboard(data);

      if (editingModificationId === modificationId) {
        cancelEditingModification();
      }

      showSuccess?.(
        t("modifications.deleted", "Project modification deleted."),
      );
    } catch (error) {
      console.error("Failed to delete project modification:", error);
      showError?.(
        error?.message ||
          t(
            "modifications.deleteFailed",
            "Failed to delete the project modification.",
          ),
      );
    } finally {
      setModificationBusy(false);
    }
  };

  /* =======================================================
     COMPLETE PROJECT
     ======================================================= */

  const completeProject = async () => {
    if (!project?.id) {
      return;
    }

    if (datesDirty) {
      const saved = await saveDates({ silentSuccess: true });

      if (!saved) {
        return;
      }
    }

    if (finalEpcDirty) {
      const saved = await saveFinalEpc({ silentSuccess: true });

      if (!saved) {
        return;
      }
    }

    setCompletionBusy(true);

    try {
      const data = await apiRequest(
        `/api/projects/${project.id}/monitoring/complete/`,
        {
          method: "POST",
        },
      );

      applyDashboard(data);
      setCompletionModalOpen(false);

      if (refreshProject) {
        await refreshProject();
      }

      showSuccess?.(
        t(
          "completion.completed",
          "The renovation project has been marked as completed.",
        ),
      );
    } catch (error) {
      console.error("Failed to complete renovation project:", error);
      showError?.(
        error?.message ||
          t("completion.failed", "Failed to complete the renovation project."),
      );
    } finally {
      setCompletionBusy(false);
    }
  };

  /* =======================================================
     NAVIGATION
     ======================================================= */

  const goToInvoices = () => {
    const invoicePath =
      String(basePath || "").startsWith("/hb") || viewerRole === "HB"
        ? "/hb/invoices"
        : "/oss/invoices";

    navigate(`${invoicePath}?project=${project.id}`);
  };

  const openAuthenticatedDocument = React.useCallback(
    async (documentInfo) => {
      const endpoint = resolveApiUrl(documentInfo?.file_url);

      if (!endpoint) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const separator = endpoint.includes("?") ? "&" : "?";

        const response = await fetch(
          `${endpoint}${separator}disposition=inline`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(
            parseApiError(
              data,
              t(
                "energy.documentOpenFailed",
                "The EPC document could not be opened.",
              ),
            ),
          );
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const openedWindow = window.open(
          objectUrl,
          "_blank",
          "noopener,noreferrer",
        );

        if (!openedWindow) {
          URL.revokeObjectURL(objectUrl);
          throw new Error(
            t("energy.popupBlocked", "Allow pop-ups to view the EPC document."),
          );
        }

        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (error) {
        console.error("Failed to open EPC document:", error);
        showError?.(
          error?.message ||
            t(
              "energy.documentOpenFailed",
              "The EPC document could not be opened.",
            ),
        );
      }
    },
    [getAccessTokenSilently, showError, t],
  );

  const openInitialEpc = () => {
    openAuthenticatedDocument(
      dashboard?.energy_performance?.pre_renovation?.document,
    );
  };

  const openFinalEpc = () => {
    openAuthenticatedDocument(
      dashboard?.energy_performance?.post_renovation?.document,
    );
  };

  /* =======================================================
     LOADING / ERROR
     ======================================================= */

  if (loading) {
    return (
      <div className="monitoring-step monitoring-step--state">
        <LoaderCircle size={30} className="monitoring-spin" />
        <strong>{t("loading.title", "Loading Monitoring")}</strong>
        <span>
          {t(
            "loading.description",
            "Collecting financing, invoice and renovation progress information...",
          )}
        </span>
      </div>
    );
  }

  if (loadError || !dashboard) {
    return (
      <div className="monitoring-step monitoring-step--state monitoring-step--error">
        <AlertCircle size={30} />

        <strong>{t("errors.title", "Monitoring could not be loaded")}</strong>

        <span>
          {loadError ||
            t("errors.loadFailed", "Failed to load monitoring information.")}
        </span>

        <button type="button" onClick={() => loadMonitoring()}>
          <RefreshCw size={15} />
          {t("common.retry", "Try again")}
        </button>
      </div>
    );
  }

  /* =======================================================
     DERIVED UI DATA
     ======================================================= */

  const financing = dashboard.financing || {};
  const tracker = dashboard.invoice_tracker || {};
  const invoices = Array.isArray(dashboard.invoices) ? dashboard.invoices : [];
  const notes = Array.isArray(dashboard.diary_notes)
    ? dashboard.diary_notes
    : [];
  const modifications = Array.isArray(dashboard.modifications)
    ? dashboard.modifications
    : [];
  const flags = dashboard.monitoring_flags || {};
  const energy = dashboard.energy_performance || {};
  const completion = dashboard.completion || {};

  const currency = financing.currency || tracker.currency || "EUR";

  const totalInvoicesValue = numericValue(tracker.total_invoices_issued) || 0;

  const totalAllocatedValue = numericValue(tracker.total_allocated) || 0;

  const lenderAllocatedValue =
    numericValue(tracker.lender_financed_invoice_amount) || 0;

  const fundingSources = buildProjectFundingSources({
    tracker,
    dashboard,
    project,
    financing,
    supplementaryFundingItems,
    t,
  });

  const lenderFundingAvailable =
    numericValue(financing.renovation_financing_amount) || 0;

  const otherFundingAvailable = fundingSources
    .filter(
      (source) => !source.isLender && source.countsTowardAvailable !== false,
    )
    .reduce((sum, source) => sum + source.availableAmount, 0);

  const otherFundingAllocated = Math.max(
    totalAllocatedValue - lenderAllocatedValue,
    0,
  );

  const totalProjectFundingAvailable =
    lenderFundingAvailable + otherFundingAvailable;

  const totalFundingRemaining = Math.max(
    totalProjectFundingAvailable - totalAllocatedValue,
    0,
  );

  const invoiceAllocationPercentage =
    totalInvoicesValue > 0
      ? clampPercentage((totalAllocatedValue / totalInvoicesValue) * 100)
      : 0;

  const overallFundingUsagePercentage =
    totalProjectFundingAvailable > 0
      ? clampPercentage(
          (totalAllocatedValue / totalProjectFundingAvailable) * 100,
        )
      : 0;

  const baselineEpc = energy.pre_renovation;
  const finalEpc = energy.post_renovation;

  const hasFinalEpcData = Boolean(
    finalEpc?.energy_rating ||
    finalEpc?.primary_non_renewable_energy_consumption ||
    finalEpc?.co2_emissions ||
    finalEpc?.certificate_date ||
    finalEpc?.document,
  );

  const finalEpcBusy = savingFinalEpc || uploadingFinalEpc || deletingFinalEpc;

  const energyImprovement =
    energy?.improvement?.primary_non_renewable_energy_consumption || {};
  const co2Improvement = energy?.improvement?.co2_emissions || {};

  return (
    <>
      <section className="monitoring-step">
        {/* ===================================================
            HEADER
            =================================================== */}

        <header className="monitoring-step__header">
          <div className="monitoring-step__heading">
            <div className="monitoring-step__eyebrow">
              <CircleGauge size={14} />
              {t("eyebrow", "Execution & results")}
            </div>

            <div className="monitoring-step__title-row">
              <h1>{t("title", "Monitoring")}</h1>

              {flags.has_delay ? (
                <span className="monitoring-alert-pill monitoring-alert-pill--delay">
                  <Clock3 size={13} />
                  {t("flags.delayed", "Delay flagged")}
                </span>
              ) : null}

              {isCompleted ? (
                <span className="monitoring-alert-pill monitoring-alert-pill--complete">
                  <CheckCircle2 size={13} />
                  {t("flags.projectCompleted", "Project completed")}
                </span>
              ) : null}
            </div>

            <p>
              {t(
                "description",
                "Track works progress, financing usage, invoices, energy performance and the final completion of the renovation.",
              )}
            </p>
          </div>

          <div className="monitoring-step__status">
            <span className="monitoring-step__stage-count">
              {t("stage", "Stage {{current}} of {{total}}", {
                current: 8,
                total: 8,
              })}
            </span>

            <StepStatusPill status={stepStatus} t={t} />

            <button
              type="button"
              className="monitoring-refresh"
              onClick={() => loadMonitoring({ silent: true })}
              disabled={refreshing}
            >
              <RefreshCw
                size={14}
                className={refreshing ? "monitoring-spin" : ""}
              />

              {refreshing
                ? t("common.refreshing", "Refreshing...")
                : t("common.refresh", "Refresh")}
            </button>
          </div>
        </header>

        {/* ===================================================
            FINANCE / INVOICE TRACKER
            =================================================== */}

        <section className="monitoring-section monitoring-section--finance">
          <SectionHeading
            icon={WalletCards}
            title={t("finance.title", "Financing & invoice tracker")}
            description={t(
              "finance.description",
              "A live view of project invoices and how they are covered across lender financing, subsidies, soft loans, homeowner funds and other funding sources.",
            )}
            trailing={
              financing.maturity ? (
                <span
                  className={`monitoring-finance-pill monitoring-finance-pill--${statusClass(
                    financing.maturity,
                  )}`}
                >
                  {t(
                    `finance.maturity.${financing.maturity}`,
                    humanizeCode(financing.maturity),
                  )}
                </span>
              ) : null
            }
          />

          <div className="monitoring-metrics-grid">
            <MetricCard
              icon={WalletCards}
              label={t(
                "finance.metrics.totalFunding",
                "Total renovation funding",
              )}
              value={formatMoney(
                totalProjectFundingAvailable,
                currency,
                locale,
              )}
              meta={t(
                "finance.metrics.totalFundingHint",
                "Lender financing plus all other recorded funding sources",
              )}
              emphasis
            />

            <MetricCard
              icon={Banknote}
              label={t(
                "finance.metrics.financing",
                "Lender renovation financing",
              )}
              value={formatMoney(lenderFundingAvailable, currency, locale)}
              meta={
                financing?.lender?.name ||
                t("finance.metrics.lenderFallback", "Lender financing")
              }
            />

            <MetricCard
              icon={Coins}
              label={t(
                "finance.metrics.otherFundingAvailable",
                "Other funding available",
              )}
              value={formatMoney(otherFundingAvailable, currency, locale)}
              meta={t(
                "finance.metrics.otherFundingAvailableHint",
                "Subsidies, soft loans, homeowner funds and other sources",
              )}
            />

            <MetricCard
              icon={FileText}
              label={t("finance.metrics.invoices", "Total invoices issued")}
              value={formatMoney(
                tracker.total_invoices_issued,
                currency,
                locale,
              )}
              meta={t(
                "finance.metrics.latestVersions",
                "Latest invoice versions",
              )}
            />

            <MetricCard
              icon={Gauge}
              label={t(
                "finance.metrics.totalAllocated",
                "Allocated to invoices",
              )}
              value={formatMoney(tracker.total_allocated, currency, locale)}
              meta={t(
                "finance.metrics.totalAllocatedHint",
                "Across all funding sources",
              )}
            />

            <MetricCard
              icon={CircleGauge}
              label={t(
                "finance.metrics.totalRemaining",
                "Funding still available",
              )}
              value={formatMoney(totalFundingRemaining, currency, locale)}
              meta={t(
                "finance.metrics.totalRemainingHint",
                "Total project funding not yet allocated to invoices",
              )}
            />
          </div>

          <div className="monitoring-progress-block">
            <div className="monitoring-progress-block__top">
              <div>
                <strong>
                  {t("finance.progress.title", "Overall project funding usage")}
                </strong>

                <span>
                  {t(
                    "finance.progress.description",
                    "{{used}} of the recorded renovation funding has been allocated to invoices.",
                    {
                      used: formatPercentage(
                        overallFundingUsagePercentage,
                        locale,
                      ),
                    },
                  )}
                </span>
              </div>

              <strong className="monitoring-progress-block__percentage">
                {formatPercentage(overallFundingUsagePercentage, locale)}
              </strong>
            </div>

            <div
              className="monitoring-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={overallFundingUsagePercentage}
            >
              <span
                style={{
                  width: `${overallFundingUsagePercentage}%`,
                }}
              />
            </div>

            <div className="monitoring-progress-block__footer">
              <span>
                {t(
                  "finance.progress.invoiceCoverage",
                  "{{percentage}} of the total invoice value has a funding allocation.",
                  {
                    percentage: formatPercentage(
                      invoiceAllocationPercentage,
                      locale,
                    ),
                  },
                )}
              </span>

              <span>
                {t("finance.progress.lastUpdate", "Last update")}:{" "}
                <strong>{formatDateTime(tracker.last_update, locale)}</strong>
              </span>
            </div>
          </div>

          <div className="monitoring-funding-overview">
            <div className="monitoring-funding-overview__header">
              <div>
                <strong>{t("finance.sourcesTitle", "Funding sources")}</strong>

                <span>
                  {t(
                    "finance.sourcesDescription",
                    "All renovation funding available to cover project invoices.",
                  )}
                </span>
              </div>

              <strong>
                {formatMoney(totalProjectFundingAvailable, currency, locale)}
              </strong>
            </div>

            {fundingSources.length ? (
              <div className="monitoring-funding-list">
                {fundingSources.map((source) => {
                  const hasAllocationDetail =
                    source.allocatedAmount !== null &&
                    source.allocatedAmount !== undefined;

                  const remaining = hasAllocationDetail
                    ? Math.max(
                        source.availableAmount - source.allocatedAmount,
                        0,
                      )
                    : null;

                  const usage =
                    hasAllocationDetail && source.availableAmount > 0
                      ? clampPercentage(
                          (source.allocatedAmount / source.availableAmount) *
                            100,
                        )
                      : null;

                  return (
                    <article
                      key={source.key}
                      className="monitoring-funding-card"
                    >
                      <div className="monitoring-funding-card__top">
                        <div>
                          <span
                            className={`monitoring-funding-card__type monitoring-funding-card__type--${statusClass(
                              source.type,
                            )}`}
                          >
                            {t(
                              `finance.sources.${source.type}`,
                              humanizeCode(source.type),
                            )}
                          </span>

                          <strong>{source.name}</strong>
                        </div>

                        <strong>
                          {formatMoney(
                            source.availableAmount,
                            currency,
                            locale,
                          )}
                        </strong>
                      </div>

                      {hasAllocationDetail ? (
                        <>
                          <div className="monitoring-funding-card__amounts">
                            <span>
                              {t("finance.sourceAllocated", "Allocated")}

                              <strong>
                                {formatMoney(
                                  source.allocatedAmount,
                                  currency,
                                  locale,
                                )}
                              </strong>
                            </span>

                            <span>
                              {t("finance.sourceRemaining", "Remaining")}

                              <strong>
                                {formatMoney(remaining, currency, locale)}
                              </strong>
                            </span>
                          </div>

                          <div
                            className="monitoring-funding-card__bar"
                            aria-hidden="true"
                          >
                            <span
                              style={{
                                width: `${usage}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="monitoring-funding-card__allocation-note">
                          <Info size={14} />

                          <span>
                            {t(
                              "finance.sourceAllocationManagedInInvoices",
                              "Invoice allocations for this source are managed in the Invoices workspace.",
                            )}
                          </span>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="monitoring-funding-empty">
                <Info size={16} />

                <span>
                  {t(
                    "finance.noFundingSources",
                    "No project funding-source breakdown is available yet.",
                  )}
                </span>
              </div>
            )}

            {numericValue(tracker.invoice_unallocated_amount) > 0 ? (
              <div className="monitoring-inline-warning">
                <AlertTriangle size={16} />

                <span>
                  {t(
                    "finance.unallocatedInvoiceValue",
                    "{{amount}} of invoice value is not yet assigned to a funding source.",
                    {
                      amount: formatMoney(
                        tracker.invoice_unallocated_amount,
                        currency,
                        locale,
                      ),
                    },
                  )}
                </span>
              </div>
            ) : null}
          </div>
        </section>

        {/* ===================================================
            DATES
            =================================================== */}

        <section className="monitoring-section">
          <SectionHeading
            icon={CalendarDays}
            title={t("dates.title", "Renovation timeline")}
            description={t(
              "dates.description",
              "Record the expected and actual completion dates. The actual completion date is required before the project can be completed.",
            )}
            trailing={
              flags.is_overdue ? (
                <span className="monitoring-overdue-pill">
                  <AlertTriangle size={13} />
                  {t("dates.overdue", "Past expected date")}
                </span>
              ) : null
            }
          />

          <div className="monitoring-date-grid">
            <label className="monitoring-field">
              <span className="monitoring-field__label">
                {t("dates.expected", "Expected completion date")}
              </span>

              <span className="monitoring-date-input-wrap">
                <CalendarDays size={15} />

                <input
                  type="date"
                  value={dates.expected_completion_date}
                  onChange={(event) =>
                    setDates((current) => ({
                      ...current,
                      expected_completion_date: event.target.value,
                    }))
                  }
                  disabled={!canEdit || isCompleted}
                />
              </span>

              <small>
                {t(
                  "dates.expectedHint",
                  "This can be adjusted if the renovation timeline changes.",
                )}
              </small>
            </label>

            <label className="monitoring-field">
              <span className="monitoring-field__label">
                {t("dates.actual", "Actual completion date")}
              </span>

              <span className="monitoring-date-input-wrap">
                <CheckCircle2 size={15} />

                <input
                  type="date"
                  value={dates.actual_completion_date}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(event) =>
                    setDates((current) => ({
                      ...current,
                      actual_completion_date: event.target.value,
                    }))
                  }
                  disabled={!canEdit || isCompleted}
                />
              </span>

              <small>
                {t(
                  "dates.actualHint",
                  "Enter this once the renovation works are finished.",
                )}
              </small>
            </label>
          </div>

          {canEdit && !isCompleted ? (
            <div className="monitoring-section__actions">
              <button
                type="button"
                className="monitoring-button monitoring-button--primary"
                onClick={() => saveDates()}
                disabled={!datesDirty || savingDates}
              >
                {savingDates ? (
                  <LoaderCircle size={16} className="monitoring-spin" />
                ) : (
                  <Save size={16} />
                )}

                {savingDates
                  ? t("dates.saving", "Saving...")
                  : t("dates.save", "Save dates")}
              </button>

              {datesDirty ? (
                <span className="monitoring-unsaved">
                  {t("common.unsavedChanges", "Unsaved changes")}
                </span>
              ) : null}
            </div>
          ) : null}
        </section>

        {/* ===================================================
            DIARY
            =================================================== */}

        <section className="monitoring-section">
          <SectionHeading
            icon={NotebookPen}
            title={t("notes.title", "Diary / notes")}
            description={t(
              "notes.description",
              "Keep shared progress notes and explain delays, milestones or important changes during the works.",
            )}
            trailing={
              <span className="monitoring-count-pill">
                {t("notes.count", "{{count}} notes", {
                  count: notes.length,
                })}
              </span>
            }
          />

          {notes.length ? (
            <div className="monitoring-entry-list">
              {notes.map((note) => {
                const editing = editingNoteId === note.id;

                const canEditNote =
                  canEdit &&
                  !isCompleted &&
                  canManageEntry(note, effectiveViewerRole, "edit");

                const canDeleteNote =
                  canEdit &&
                  !isCompleted &&
                  canManageEntry(note, effectiveViewerRole, "delete");

                return (
                  <article
                    key={note.id}
                    className={`monitoring-entry ${
                      note.tag === "DELAY" ? "monitoring-entry--delay" : ""
                    }`}
                  >
                    {editing ? (
                      <div className="monitoring-entry__edit">
                        <div className="monitoring-form-grid monitoring-form-grid--note">
                          <label className="monitoring-field">
                            <span className="monitoring-field__label">
                              {t("notes.tag", "Tag")}
                            </span>

                            <span className="monitoring-select-wrap">
                              <select
                                value={editingNote.tag}
                                onChange={(event) =>
                                  setEditingNote((current) => ({
                                    ...current,
                                    tag: event.target.value,
                                  }))
                                }
                              >
                                <option value="UPDATE">
                                  {t("notes.tags.UPDATE", "Update")}
                                </option>
                                <option value="DELAY">
                                  {t("notes.tags.DELAY", "Delay")}
                                </option>
                              </select>

                              <ChevronDown size={15} />
                            </span>
                          </label>

                          <label className="monitoring-field monitoring-field--grow">
                            <span className="monitoring-field__label">
                              {t("notes.note", "Note")}
                            </span>

                            <textarea
                              rows={3}
                              value={editingNote.note}
                              onChange={(event) =>
                                setEditingNote((current) => ({
                                  ...current,
                                  note: event.target.value,
                                }))
                              }
                            />
                          </label>
                        </div>

                        <div className="monitoring-entry__edit-actions">
                          <button
                            type="button"
                            className="monitoring-button monitoring-button--secondary"
                            onClick={cancelEditingNote}
                            disabled={noteBusy}
                          >
                            <X size={15} />
                            {t("common.cancel", "Cancel")}
                          </button>

                          <button
                            type="button"
                            className="monitoring-button monitoring-button--primary"
                            onClick={() => updateNote(note.id)}
                            disabled={noteBusy}
                          >
                            {noteBusy ? (
                              <LoaderCircle
                                size={15}
                                className="monitoring-spin"
                              />
                            ) : (
                              <Save size={15} />
                            )}
                            {t("common.save", "Save")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="monitoring-entry__head">
                          <div className="monitoring-entry__identity">
                            <span
                              className={`monitoring-note-tag monitoring-note-tag--${statusClass(
                                note.tag,
                              )}`}
                            >
                              {note.tag === "DELAY" ? (
                                <AlertTriangle size={12} />
                              ) : (
                                <Sparkles size={12} />
                              )}

                              {t(
                                `notes.tags.${note.tag}`,
                                humanizeCode(note.tag),
                              )}
                            </span>

                            <span>
                              {note.created_by?.name ||
                                t("common.platformUser", "Platform user")}
                            </span>
                          </div>

                          <time>{formatDateTime(note.created_at, locale)}</time>
                        </div>

                        <p className="monitoring-entry__text">{note.note}</p>

                        {canEditNote || canDeleteNote ? (
                          <div className="monitoring-entry__actions">
                            {canEditNote ? (
                              <button
                                type="button"
                                onClick={() => startEditingNote(note)}
                                disabled={noteBusy}
                                aria-label={t("common.edit", "Edit")}
                              >
                                <Pencil size={14} />
                                {t("common.edit", "Edit")}
                              </button>
                            ) : null}

                            {canDeleteNote ? (
                              <button
                                type="button"
                                className="monitoring-entry__delete"
                                onClick={() => deleteNote(note.id)}
                                disabled={noteBusy}
                                aria-label={t("common.delete", "Delete")}
                              >
                                <Trash2 size={14} />
                                {t("common.delete", "Delete")}
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <span className="monitoring-entry__ownership">
                            <ShieldCheck size={13} />

                            {t("common.roleOwned", "Managed by {{role}}", {
                              role:
                                getEntryCreatorRole(note) ||
                                t("common.originalAuthor", "original author"),
                            })}
                          </span>
                        )}
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="monitoring-empty-state">
              <NotebookPen size={22} />
              <strong>
                {t("notes.emptyTitle", "No monitoring notes yet")}
              </strong>
              <span>
                {t(
                  "notes.emptyDescription",
                  "Progress updates and delays recorded during the works will appear here.",
                )}
              </span>
            </div>
          )}

          {canEdit && !isCompleted ? (
            <div className="monitoring-composer">
              <div className="monitoring-composer__head">
                <Plus size={15} />
                <strong>{t("notes.addTitle", "Add diary entry")}</strong>
              </div>

              <div className="monitoring-form-grid monitoring-form-grid--note">
                <label className="monitoring-field monitoring-field--grow">
                  <span className="monitoring-field__label">
                    {t("notes.note", "Note")}
                  </span>

                  <textarea
                    rows={3}
                    value={noteForm.note}
                    onChange={(event) =>
                      setNoteForm((current) => ({
                        ...current,
                        note: event.target.value,
                      }))
                    }
                    placeholder={t(
                      "notes.placeholder",
                      "Example: Permit approval delayed the expected completion by two weeks.",
                    )}
                  />
                </label>

                <div className="monitoring-composer__side">
                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("notes.tag", "Tag")}
                    </span>

                    <span className="monitoring-select-wrap">
                      <select
                        value={noteForm.tag}
                        onChange={(event) =>
                          setNoteForm((current) => ({
                            ...current,
                            tag: event.target.value,
                          }))
                        }
                      >
                        <option value="UPDATE">
                          {t("notes.tags.UPDATE", "Update")}
                        </option>
                        <option value="DELAY">
                          {t("notes.tags.DELAY", "Delay")}
                        </option>
                      </select>

                      <ChevronDown size={15} />
                    </span>
                  </label>

                  <button
                    type="button"
                    className="monitoring-button monitoring-button--accent"
                    onClick={createNote}
                    disabled={noteBusy || !noteForm.note.trim()}
                  >
                    {noteBusy ? (
                      <LoaderCircle size={15} className="monitoring-spin" />
                    ) : (
                      <Plus size={15} />
                    )}

                    {t("notes.add", "Add note")}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        {/* ===================================================
            EPC + INVOICE SUMMARY
            =================================================== */}

        <div className="monitoring-two-column">
          <section className="monitoring-section monitoring-section--energy">
            <SectionHeading
              icon={Leaf}
              title={t("energy.title", "Energy performance")}
              description={t(
                "energy.description",
                "Compare the renovation baseline with the Final EPC and review the measured improvement.",
              )}
              trailing={
                !hasFinalEpcData ? (
                  <span className="monitoring-optional-pill">
                    {t("energy.finalPending", "Final EPC pending")}
                  </span>
                ) : (
                  <span className="monitoring-complete-pill">
                    <Check size={12} />
                    {t("energy.finalAvailable", "Final EPC available")}
                  </span>
                )
              }
            />

            <div className="monitoring-epc-comparison monitoring-epc-comparison--editable">
              <article className="monitoring-epc-card monitoring-epc-card--baseline">
                <div className="monitoring-epc-card__topline">
                  <span className="monitoring-epc-card__label">
                    {t("energy.before", "Before renovation")}
                  </span>

                  <span className="monitoring-epc-card__readonly-pill">
                    {t("energy.baselineReadonly", "Baseline · read only")}
                  </span>
                </div>

                <div className="monitoring-epc-card__main">
                  <span
                    className={`monitoring-epc-grade ${energyRatingClass(
                      baselineEpc?.energy_rating,
                    )}`}
                  >
                    {displayEnergyRating(baselineEpc?.energy_rating)}
                  </span>

                  <div>
                    <strong>
                      {baselineEpc?.primary_non_renewable_energy_consumption
                        ? `${formatDecimalInput(
                            baselineEpc.primary_non_renewable_energy_consumption,
                          )} kWh/m²·yr`
                        : "—"}
                    </strong>

                    <small>
                      {baselineEpc?.co2_emissions
                        ? `${formatDecimalInput(
                            baselineEpc.co2_emissions,
                          )} kgCO₂/m²·yr`
                        : t("energy.noEmissionData", "No emissions value")}
                    </small>
                  </div>
                </div>

                <div className="monitoring-epc-card__meta-grid">
                  <div>
                    <span>
                      {t("energy.certificateDate", "Certificate date")}
                    </span>
                    <strong>
                      {formatDate(baselineEpc?.certificate_date, locale)}
                    </strong>
                  </div>

                  <div>
                    <span>{t("energy.certifier", "Certifier")}</span>
                    <strong>{baselineEpc?.certifier_name || "—"}</strong>
                  </div>
                </div>

                {baselineEpc?.document ? (
                  <button
                    type="button"
                    className="monitoring-document-button monitoring-document-button--baseline"
                    onClick={openInitialEpc}
                  >
                    <FileText size={16} />

                    <span>
                      <strong>
                        {t("energy.initialDocument", "Initial EPC certificate")}
                      </strong>
                      <small>{baselineEpc.document.filename}</small>
                    </span>

                    <ArrowUpRight size={15} />
                  </button>
                ) : (
                  <div className="monitoring-energy-empty monitoring-energy-empty--baseline">
                    <FileText size={18} />
                    <span>
                      {t(
                        "energy.noInitialDocument",
                        "The initial EPC document is not available in Monitoring.",
                      )}
                    </span>
                  </div>
                )}
              </article>

              <span className="monitoring-epc-arrow" aria-hidden="true">
                <ArrowUpRight size={19} />
              </span>

              <article className="monitoring-epc-card monitoring-epc-card--final monitoring-epc-card--editable">
                <div className="monitoring-epc-card__topline">
                  <span className="monitoring-epc-card__label">
                    {t("energy.after", "Final EPC")}
                  </span>

                  {hasFinalEpcData ? (
                    <span className="monitoring-epc-card__final-pill">
                      <Check size={12} />
                      {t("energy.finalRecorded", "Recorded")}
                    </span>
                  ) : null}
                </div>

                <div className="monitoring-final-epc-form">
                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.energyRating", "EPC label")}
                    </span>

                    <span className="monitoring-select-wrap">
                      <select
                        value={finalEpcForm.energy_rating}
                        onChange={(event) =>
                          setFinalEpcForm((current) => ({
                            ...current,
                            energy_rating: event.target.value,
                          }))
                        }
                        disabled={!canEdit || isCompleted || finalEpcBusy}
                      >
                        <option value="">
                          {t("energy.selectRating", "Select EPC label")}
                        </option>

                        {EPC_RATING_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={15} />
                    </span>
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.emissionRating", "Emission rating")}
                    </span>

                    <span className="monitoring-select-wrap">
                      <select
                        value={finalEpcForm.emission_rating}
                        onChange={(event) =>
                          setFinalEpcForm((current) => ({
                            ...current,
                            emission_rating: event.target.value,
                          }))
                        }
                        disabled={!canEdit || isCompleted || finalEpcBusy}
                      >
                        <option value="">
                          {t("energy.selectEmissionRating", "Select rating")}
                        </option>

                        {EPC_RATING_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={15} />
                    </span>
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.primaryEnergy", "Energy consumption")}
                    </span>

                    <span className="monitoring-unit-input">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatDecimalInput(
                          finalEpcForm.primary_non_renewable_energy_consumption,
                        )}
                        onChange={(event) =>
                          setFinalEpcForm((current) => ({
                            ...current,
                            primary_non_renewable_energy_consumption:
                              sanitizeDecimalInput(event.target.value, 2),
                          }))
                        }
                        placeholder="145.00"
                        disabled={!canEdit || isCompleted || finalEpcBusy}
                      />

                      <small>kWh/m²·yr</small>
                    </span>
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.co2", "CO₂ emissions")}
                    </span>

                    <span className="monitoring-unit-input">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatDecimalInput(finalEpcForm.co2_emissions)}
                        onChange={(event) =>
                          setFinalEpcForm((current) => ({
                            ...current,
                            co2_emissions: sanitizeDecimalInput(
                              event.target.value,
                              2,
                            ),
                          }))
                        }
                        placeholder="22.00"
                        disabled={!canEdit || isCompleted || finalEpcBusy}
                      />

                      <small>kgCO₂/m²·yr</small>
                    </span>
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.certificateDate", "Certificate date")}
                    </span>

                    <input
                      type="date"
                      value={finalEpcForm.certificate_date}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(event) =>
                        setFinalEpcForm((current) => ({
                          ...current,
                          certificate_date: event.target.value,
                        }))
                      }
                      disabled={!canEdit || isCompleted || finalEpcBusy}
                    />
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.certifier", "Certifier")}
                    </span>

                    <input
                      type="text"
                      value={finalEpcForm.certifier_name}
                      onChange={(event) =>
                        setFinalEpcForm((current) => ({
                          ...current,
                          certifier_name: event.target.value,
                        }))
                      }
                      placeholder={t(
                        "energy.certifierPlaceholder",
                        "Name of certifier or professional",
                      )}
                      disabled={!canEdit || isCompleted || finalEpcBusy}
                    />
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.accreditation", "Professional accreditation")}
                    </span>

                    <span className="monitoring-select-wrap">
                      <select
                        value={finalEpcForm.professional_accreditation}
                        onChange={(event) =>
                          setFinalEpcForm((current) => ({
                            ...current,
                            professional_accreditation: event.target.value,
                          }))
                        }
                        disabled={!canEdit || isCompleted || finalEpcBusy}
                      >
                        <option value="">
                          {t(
                            "energy.selectAccreditation",
                            "Select accreditation",
                          )}
                        </option>

                        {EPC_ACCREDITATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(
                              `energy.accreditations.${option.value}`,
                              option.label,
                            )}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={15} />
                    </span>
                  </label>

                  <label className="monitoring-field">
                    <span className="monitoring-field__label">
                      {t("energy.sector", "Sector")}
                    </span>

                    <span className="monitoring-select-wrap">
                      <select
                        value={finalEpcForm.sector}
                        onChange={(event) =>
                          setFinalEpcForm((current) => ({
                            ...current,
                            sector: event.target.value,
                          }))
                        }
                        disabled={!canEdit || isCompleted || finalEpcBusy}
                      >
                        <option value="">
                          {t("energy.selectSector", "Select sector")}
                        </option>

                        {EPC_SECTOR_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(`energy.sectors.${option.value}`, option.label)}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={15} />
                    </span>
                  </label>

                  <label className="monitoring-field monitoring-field--full">
                    <span className="monitoring-field__label">
                      {t("energy.notes", "Final EPC notes")}
                    </span>

                    <textarea
                      rows={3}
                      value={finalEpcForm.notes}
                      onChange={(event) =>
                        setFinalEpcForm((current) => ({
                          ...current,
                          notes: event.target.value,
                        }))
                      }
                      placeholder={t(
                        "energy.notesPlaceholder",
                        "Optional notes about the final energy certificate.",
                      )}
                      disabled={!canEdit || isCompleted || finalEpcBusy}
                    />
                  </label>
                </div>

                {canEdit && !isCompleted ? (
                  <div className="monitoring-final-epc-actions">
                    <button
                      type="button"
                      className="monitoring-button monitoring-button--primary"
                      onClick={() => saveFinalEpc()}
                      disabled={
                        !finalEpcDirty || savingFinalEpc || finalEpcBusy
                      }
                    >
                      {savingFinalEpc ? (
                        <LoaderCircle size={15} className="monitoring-spin" />
                      ) : (
                        <Save size={15} />
                      )}

                      {savingFinalEpc
                        ? t("energy.saving", "Saving...")
                        : t("energy.save", "Save Final EPC")}
                    </button>

                    {finalEpcDirty ? (
                      <span className="monitoring-unsaved">
                        {t("common.unsavedChanges", "Unsaved changes")}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                <div className="monitoring-final-epc-document">
                  <input
                    ref={finalEpcFileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="monitoring-file-input"
                    onChange={(event) => {
                      const file = event.target.files?.[0];

                      if (file) {
                        uploadFinalEpcDocument(file);
                      }
                    }}
                    disabled={!canEdit || isCompleted || finalEpcBusy}
                  />

                  {finalEpc?.document ? (
                    <div className="monitoring-final-epc-document__available">
                      <button
                        type="button"
                        className="monitoring-document-button"
                        onClick={openFinalEpc}
                      >
                        <FileCheck2 size={16} />

                        <span>
                          <strong>
                            {t("energy.finalDocument", "Final EPC certificate")}
                          </strong>
                          <small>{finalEpc.document.filename}</small>
                        </span>

                        <ArrowUpRight size={15} />
                      </button>

                      {canEdit && !isCompleted ? (
                        <div className="monitoring-final-epc-document__actions">
                          <button
                            type="button"
                            className="monitoring-button monitoring-button--secondary"
                            onClick={() =>
                              finalEpcFileInputRef.current?.click()
                            }
                            disabled={finalEpcBusy}
                          >
                            {uploadingFinalEpc ? (
                              <LoaderCircle
                                size={14}
                                className="monitoring-spin"
                              />
                            ) : (
                              <RefreshCw size={14} />
                            )}
                            {t("energy.replaceDocument", "Replace")}
                          </button>

                          <button
                            type="button"
                            className="monitoring-button monitoring-button--danger"
                            onClick={deleteFinalEpcDocument}
                            disabled={finalEpcBusy}
                          >
                            {deletingFinalEpc ? (
                              <LoaderCircle
                                size={14}
                                className="monitoring-spin"
                              />
                            ) : (
                              <Trash2 size={14} />
                            )}
                            {t("energy.removeDocument", "Remove")}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="monitoring-final-epc-upload">
                      <FileText size={20} />

                      <div>
                        <strong>
                          {t(
                            "energy.noFinalDocumentTitle",
                            "Final EPC certificate not uploaded",
                          )}
                        </strong>
                        <span>
                          {t(
                            "energy.noFinalDocument",
                            "Upload the official PDF when it becomes available. The structured EPC values can be saved independently.",
                          )}
                        </span>
                      </div>

                      {canEdit && !isCompleted ? (
                        <button
                          type="button"
                          className="monitoring-button monitoring-button--secondary"
                          onClick={() => finalEpcFileInputRef.current?.click()}
                          disabled={finalEpcBusy}
                        >
                          {uploadingFinalEpc ? (
                            <LoaderCircle
                              size={14}
                              className="monitoring-spin"
                            />
                          ) : (
                            <Plus size={14} />
                          )}
                          {t("energy.uploadDocument", "Upload PDF")}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </article>
            </div>

            <div className="monitoring-improvement-grid">
              <div>
                <TrendingDown size={15} />
                <span>{t("energy.energyReduction", "Energy reduction")}</span>
                <strong>
                  {formatPercentage(
                    energyImprovement.reduction_percentage,
                    locale,
                  )}
                </strong>
              </div>

              <div>
                <Leaf size={15} />
                <span>{t("energy.co2Reduction", "CO₂ reduction")}</span>
                <strong>
                  {formatPercentage(
                    co2Improvement.reduction_percentage,
                    locale,
                  )}
                </strong>
              </div>
            </div>
          </section>

          <section className="monitoring-section monitoring-section--invoices">
            <SectionHeading
              icon={FileText}
              title={t("invoices.title", "Invoices summary")}
              description={t(
                "invoices.description",
                "Latest-version invoice values and their current project status.",
              )}
              trailing={
                <span className="monitoring-count-pill">
                  {t("invoices.count", "{{count}} invoices", {
                    count: invoices.length,
                  })}
                </span>
              }
            />

            {invoices.length ? (
              <div className="monitoring-invoice-list">
                {invoices.slice(0, 6).map((invoice) => (
                  <article key={invoice.id} className="monitoring-invoice-row">
                    <div className="monitoring-invoice-row__name">
                      <FileText size={15} />

                      <div>
                        <strong>
                          {invoice.latest_filename ||
                            t("invoices.invoiceFallback", "Invoice #{{id}}", {
                              id: invoice.id,
                            })}
                        </strong>

                        <span>{humanizeCode(invoice.renovation_type)}</span>

                        <small>
                          {t(
                            "invoices.fundingSummary",
                            "{{allocated}} allocated · {{lender}} lender",
                            {
                              allocated: formatMoney(
                                invoice.allocated_amount,
                                currency,
                                locale,
                              ),
                              lender: formatMoney(
                                invoice.lender_financed_amount,
                                currency,
                                locale,
                              ),
                            },
                          )}
                        </small>
                      </div>
                    </div>

                    <span
                      className={`monitoring-invoice-status monitoring-invoice-status--${statusClass(
                        invoice.current_status || invoice.oss_decision,
                      )}`}
                    >
                      {humanizeCode(
                        invoice.current_status || invoice.oss_decision,
                      )}
                    </span>

                    <strong>
                      {formatMoney(invoice.latest_amount, currency, locale)}
                    </strong>
                  </article>
                ))}
              </div>
            ) : (
              <div className="monitoring-empty-state monitoring-empty-state--compact">
                <FileText size={20} />
                <strong>{t("invoices.emptyTitle", "No invoices yet")}</strong>
                <span>
                  {t(
                    "invoices.emptyDescription",
                    "Invoices linked to this renovation project will appear here.",
                  )}
                </span>
              </div>
            )}

            <div className="monitoring-invoices-footer">
              <div>
                <span>{t("invoices.allocated", "Total allocated")}</span>

                <strong>
                  {formatMoney(tracker.total_allocated, currency, locale)}
                </strong>
              </div>

              <div>
                <span>{t("invoices.lenderAllocated", "Lender allocated")}</span>

                <strong>
                  {formatMoney(lenderAllocatedValue, currency, locale)}
                </strong>
              </div>

              <div>
                <span>
                  {t("invoices.otherFunding", "Other funding allocated")}
                </span>

                <strong>
                  {formatMoney(otherFundingAllocated, currency, locale)}
                </strong>
              </div>

              <div>
                <span>{t("invoices.unallocated", "Still unallocated")}</span>

                <strong>
                  {formatMoney(
                    tracker.invoice_unallocated_amount,
                    currency,
                    locale,
                  )}
                </strong>
              </div>
            </div>

            <button
              type="button"
              className="monitoring-button monitoring-button--secondary monitoring-button--full"
              onClick={goToInvoices}
            >
              <ArrowUpRight size={15} />
              {t("invoices.open", "Go to invoices")}
            </button>
          </section>
        </div>

        {/* ===================================================
            MODIFICATIONS
            =================================================== */}

        <section className="monitoring-section">
          <SectionHeading
            icon={ClipboardCheck}
            title={t("modifications.title", "Project modifications")}
            description={t(
              "modifications.description",
              "Record traceable scope, timeline, cost or extension changes that occur during implementation.",
            )}
            trailing={
              <span className="monitoring-count-pill">
                {t("modifications.count", "{{count}} items", {
                  count: modifications.length,
                })}
              </span>
            }
          />

          {modifications.length ? (
            <div className="monitoring-entry-list">
              {modifications.map((item) => {
                const editing = editingModificationId === item.id;

                const canEditModification =
                  canEdit &&
                  !isCompleted &&
                  canManageEntry(item, effectiveViewerRole, "edit");

                const canDeleteModification =
                  canEdit &&
                  !isCompleted &&
                  canManageEntry(item, effectiveViewerRole, "delete");

                return (
                  <article key={item.id} className="monitoring-entry">
                    {editing ? (
                      <div className="monitoring-entry__edit">
                        <div className="monitoring-modification-types">
                          {MODIFICATION_TYPES.map((type) => (
                            <label
                              key={type}
                              className={`monitoring-check-pill ${
                                editingModification.modification_types.includes(
                                  type,
                                )
                                  ? "monitoring-check-pill--selected"
                                  : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={editingModification.modification_types.includes(
                                  type,
                                )}
                                onChange={() =>
                                  toggleModificationType(type, true)
                                }
                              />

                              <span>
                                {t(
                                  `modifications.types.${type}`,
                                  humanizeCode(type),
                                )}
                              </span>
                            </label>
                          ))}
                        </div>

                        <label className="monitoring-field">
                          <span className="monitoring-field__label">
                            {t("modifications.descriptionLabel", "Description")}
                          </span>

                          <textarea
                            rows={3}
                            value={editingModification.description}
                            onChange={(event) =>
                              setEditingModification((current) => ({
                                ...current,
                                description: event.target.value,
                              }))
                            }
                          />
                        </label>

                        <div className="monitoring-entry__edit-actions">
                          <button
                            type="button"
                            className="monitoring-button monitoring-button--secondary"
                            onClick={cancelEditingModification}
                            disabled={modificationBusy}
                          >
                            <X size={15} />
                            {t("common.cancel", "Cancel")}
                          </button>

                          <button
                            type="button"
                            className="monitoring-button monitoring-button--primary"
                            onClick={() => updateModification(item.id)}
                            disabled={modificationBusy}
                          >
                            {modificationBusy ? (
                              <LoaderCircle
                                size={15}
                                className="monitoring-spin"
                              />
                            ) : (
                              <Save size={15} />
                            )}
                            {t("common.save", "Save")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="monitoring-entry__head">
                          <div className="monitoring-entry__chips">
                            {(item.modification_types || []).length ? (
                              item.modification_types.map((type) => (
                                <span
                                  key={type}
                                  className="monitoring-type-pill"
                                >
                                  {t(
                                    `modifications.types.${type}`,
                                    humanizeCode(type),
                                  )}
                                </span>
                              ))
                            ) : (
                              <span className="monitoring-type-pill monitoring-type-pill--neutral">
                                {t("modifications.types.OTHER", "General")}
                              </span>
                            )}
                          </div>

                          <time>{formatDateTime(item.created_at, locale)}</time>
                        </div>

                        <p className="monitoring-entry__text">
                          {item.description}
                        </p>

                        <div className="monitoring-entry__footer">
                          <span>
                            {item.created_by?.name ||
                              t("common.platformUser", "Platform user")}
                          </span>

                          {canEditModification || canDeleteModification ? (
                            <div className="monitoring-entry__actions">
                              {canEditModification ? (
                                <button
                                  type="button"
                                  onClick={() => startEditingModification(item)}
                                  disabled={modificationBusy}
                                >
                                  <Pencil size={14} />
                                  {t("common.edit", "Edit")}
                                </button>
                              ) : null}

                              {canDeleteModification ? (
                                <button
                                  type="button"
                                  className="monitoring-entry__delete"
                                  onClick={() => deleteModification(item.id)}
                                  disabled={modificationBusy}
                                >
                                  <Trash2 size={14} />
                                  {t("common.delete", "Delete")}
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <span className="monitoring-entry__ownership">
                              <ShieldCheck size={13} />

                              {t("common.roleOwned", "Managed by {{role}}", {
                                role:
                                  getEntryCreatorRole(item) ||
                                  t("common.originalAuthor", "original author"),
                              })}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="monitoring-empty-state">
              <ClipboardCheck size={22} />
              <strong>
                {t("modifications.emptyTitle", "No project modifications")}
              </strong>
              <span>
                {t(
                  "modifications.emptyDescription",
                  "Changes to scope, timeline or cost can be recorded here when they occur.",
                )}
              </span>
            </div>
          )}

          {canEdit && !isCompleted ? (
            <div className="monitoring-composer">
              <div className="monitoring-composer__head">
                <Plus size={15} />
                <strong>
                  {t("modifications.addTitle", "Add project modification")}
                </strong>
              </div>

              <div className="monitoring-modification-types">
                {MODIFICATION_TYPES.map((type) => (
                  <label
                    key={type}
                    className={`monitoring-check-pill ${
                      modificationForm.modification_types.includes(type)
                        ? "monitoring-check-pill--selected"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={modificationForm.modification_types.includes(
                        type,
                      )}
                      onChange={() => toggleModificationType(type)}
                    />

                    <span>
                      {t(`modifications.types.${type}`, humanizeCode(type))}
                    </span>
                  </label>
                ))}
              </div>

              <label className="monitoring-field">
                <span className="monitoring-field__label">
                  {t("modifications.descriptionLabel", "Description")}
                </span>

                <textarea
                  rows={3}
                  value={modificationForm.description}
                  onChange={(event) =>
                    setModificationForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  placeholder={t(
                    "modifications.placeholder",
                    "Example: Additional insulation was approved following an on-site inspection.",
                  )}
                />
              </label>

              <div className="monitoring-composer__footer">
                <span>
                  {t(
                    "modifications.traceableHint",
                    "Changes remain traceable in the project audit trail.",
                  )}
                </span>

                <button
                  type="button"
                  className="monitoring-button monitoring-button--accent"
                  onClick={createModification}
                  disabled={
                    modificationBusy || !modificationForm.description.trim()
                  }
                >
                  {modificationBusy ? (
                    <LoaderCircle size={15} className="monitoring-spin" />
                  ) : (
                    <Plus size={15} />
                  )}

                  {t("modifications.add", "Add modification")}
                </button>
              </div>
            </div>
          ) : null}
        </section>

        {/* ===================================================
            COMPLETION
            =================================================== */}

        <section
          className={`monitoring-completion ${
            isCompleted ? "monitoring-completion--done" : ""
          }`}
        >
          <span className="monitoring-completion__icon">
            {isCompleted ? (
              <CheckCircle2 size={22} />
            ) : (
              <ClipboardCheck size={22} />
            )}
          </span>

          <div className="monitoring-completion__content">
            <h2>
              {isCompleted
                ? t("completion.doneTitle", "Renovation project completed")
                : t("completion.title", "Project completion")}
            </h2>

            <p>
              {isCompleted
                ? t(
                    "completion.doneDescription",
                    "The project has been completed and Monitoring is now read-only.",
                  )
                : t(
                    "completion.description",
                    "Once the works are finished, save the actual completion date and complete the project.",
                  )}
            </p>

            {!isCompleted && completion.warnings?.length ? (
              <div className="monitoring-completion__warnings">
                {completion.warnings.includes("FINAL_EPC_NOT_AVAILABLE") ? (
                  <span>
                    <AlertTriangle size={14} />
                    {t(
                      "completion.warnings.finalEpcMissing",
                      "Final EPC information is not available.",
                    )}
                  </span>
                ) : null}

                {completion.warnings.includes(
                  "FINAL_EPC_DOCUMENT_NOT_AVAILABLE",
                ) ? (
                  <span>
                    <AlertTriangle size={14} />
                    {t(
                      "completion.warnings.finalEpcDocumentMissing",
                      "Final EPC evidence has not been uploaded.",
                    )}
                  </span>
                ) : null}
              </div>
            ) : null}

            {isCompleted && dates.actual_completion_date ? (
              <span className="monitoring-completion__date">
                {t("completion.completedOn", "Completed on")}{" "}
                <strong>
                  {formatDate(dates.actual_completion_date, locale)}
                </strong>
              </span>
            ) : null}
          </div>

          {!isCompleted ? (
            <button
              type="button"
              className="monitoring-button monitoring-button--complete"
              disabled={
                !dates.actual_completion_date || completionBusy || !canEdit
              }
              onClick={() => setCompletionModalOpen(true)}
            >
              <CheckCircle2 size={16} />
              {t("completion.action", "Complete project")}
            </button>
          ) : (
            <span className="monitoring-completion__done-pill">
              <Check size={14} />
              {t("status.completed", "Completed")}
            </span>
          )}
        </section>
      </section>

      <CompletionModal
        open={completionModalOpen}
        onClose={() => {
          if (!completionBusy) {
            setCompletionModalOpen(false);
          }
        }}
        onConfirm={completeProject}
        busy={completionBusy}
        warnings={completion.warnings || []}
        t={t}
      />
    </>
  );
}
